import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Cameras from './pages/Cameras';
import PlateRecognition from './pages/PlateRecognition';
import Tickets from './pages/Tickets';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Monitoring from './pages/Monitoring';
import PriceTables from './pages/PriceTables';
import Customers from './pages/Customers';
import MonthlyPlans from './pages/MonthlyPlans';
import Accredited from './pages/Accredited';
import Agreements from './pages/Agreements';
import PDV from './pages/PDV';
import Residents from './pages/Residents';           // Fase 4
import Visitors from './pages/Visitors';             // Fase 4
import AccessReports from './pages/AccessReports';   // Fase 4
import ApiIntegration from './pages/ApiIntegration'; // Fase 5
import GateLog from './pages/GateLog';                   // Fase 5.1
import ClubSocial from './pages/ClubSocial';
import IntegrationDocs from './pages/IntegrationDocs';    // Onda 64
import ApiRequestLogPage from './pages/ApiRequestLogPage'; // Onda 66
import IntegrationLoads from './pages/IntegrationLoads';   // Onda 109
import ClubMembers from './pages/ClubMembers';             // Onda 110
import OcrQuality from './pages/OcrQuality';           // Onda 15
import Saidas from './pages/Saidas';                   // Onda 183
import Reconciliation from './pages/Reconciliation';   // Onda 16
import Quarantine from './pages/Quarantine';           // Onda 35
import Inventory from './pages/Inventory';             // Onda 46
import HelpCenter from './pages/HelpCenter';           // Onda 92
import MinhaConta from './pages/MinhaConta';           // Onda 150
function App() {
  // ═══ Onda 78 ═══════════════════════════════════════════════════════════════
  //
  // Estas duas coisas — o cliente do socket e o <Toaster /> — existiam e foram
  // removidas por engano no commit f253640 ("Fase 3 — PDV"), de 27/05/2026. O
  // commit era sobre o caixa; reescreveu o App.js e levou as duas junto. A
  // própria mensagem dele termina com "Preservação total de todas as
  // funcionalidades das Fases 1 e 2".
  //
  // O que ficou quebrado desde então:
  //
  //   - o backend seguiu emitindo "monitoring_update" e "access_denied"
  //     (rtspService.js), o socket.io-client seguiu no package.json e o
  //     AuthProvider seguiu ACEITANDO a prop socket — mas ninguém a passava,
  //     então socket era sempre undefined e todo handler caía no
  //     "if (!socket) return". O Monitoramento passou a depender só do polling
  //     de 5 s, e o alerta sonoro de acesso negado, que não tem equivalente em
  //     polling, simplesmente parou de tocar;
  //
  //   - sem o <Toaster /> montado, NENHUMA das 137 chamadas de toast espalhadas
  //     por 19 telas renderizava. O operador salvava um cadastro e não recebia
  //     confirmação; uma chamada falhava e ele não via o erro.
  //
  // REACT_APP_SOCKET_URL não está definida (não há .env no frontend), e é o que
  // queremos: io(undefined) conecta na ORIGEM DA PRÓPRIA PÁGINA. Assim a mesma
  // build serve o acesso pela rede local e o acesso por g2.acessoid.com.br, sem
  // endereço fixo no bundle.
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const novo = io(process.env.REACT_APP_SOCKET_URL, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });
    setSocket(novo);
    return () => novo.disconnect();
  }, []);

  return (
    <AuthProvider socket={socket}>
      <Router>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* Operação */}
          <Route path="/dashboard"         element={<PrivateRoute feature="dashboard"><Layout><Dashboard /></Layout></PrivateRoute>} />
          {/* Onda 150: todo usuário logado, sem funcionalidade exigida — é a conta dele. */}
          <Route path="/minha-conta"        element={<PrivateRoute><Layout><MinhaConta /></Layout></PrivateRoute>} />
          <Route path="/monitoring"         element={<PrivateRoute feature="monitoring"><Layout><Monitoring /></Layout></PrivateRoute>} />
          <Route path="/cameras"            element={<PrivateRoute feature="cameras_view"><Layout><Cameras /></Layout></PrivateRoute>} />
          <Route path="/plate-recognition"  element={<PrivateRoute feature="plate_recognition"><Layout><PlateRecognition /></Layout></PrivateRoute>} />
          <Route path="/tickets"            element={<PrivateRoute feature="tickets"><Layout><Tickets /></Layout></PrivateRoute>} />
          <Route path="/reports"            element={<PrivateRoute feature="reports_operation"><Layout><Reports /></Layout></PrivateRoute>} />
          {/* Onda 92: a Central de Ajuda alcança até o perfil de consulta —
              quem mais precisa de manual é quem tem menos permissão. */}
          <Route path="/ajuda"              element={<PrivateRoute feature="help_center"><Layout><HelpCenter /></Layout></PrivateRoute>} />
          {/* Caixa (PDV) */}
          <Route path="/pdv"                element={<PrivateRoute feature="pdv"><Layout><PDV /></Layout></PrivateRoute>} />
          {/* Cadastros */}
          <Route path="/customers"          element={<PrivateRoute feature="customers"><Layout><Customers /></Layout></PrivateRoute>} />
          <Route path="/monthly-plans"      element={<PrivateRoute feature="monthly_plans"><Layout><MonthlyPlans /></Layout></PrivateRoute>} />
          <Route path="/accredited"         element={<PrivateRoute feature="accredited"><Layout><Accredited /></Layout></PrivateRoute>} />
          <Route path="/agreements"         element={<PrivateRoute feature="agreements"><Layout><Agreements /></Layout></PrivateRoute>} />
          {/* Fase 4 — Vertical Condomínio */}
          <Route path="/residents"          element={<PrivateRoute feature="residents"><Layout><Residents /></Layout></PrivateRoute>} />
          <Route path="/visitors"           element={<PrivateRoute feature="visitors"><Layout><Visitors /></Layout></PrivateRoute>} />
          {/* Onda 41: dados pessoais dos moradores — exige o papel, não só a sessão */}
          <Route path="/access-reports"     element={<PrivateRoute feature="access_reports"><Layout><AccessReports /></Layout></PrivateRoute>} />
          {/* Vertical Clube Social */}
          <Route path="/club-social"         element={<PrivateRoute feature="club_social"><Layout><ClubSocial /></Layout></PrivateRoute>} />
          <Route path="/integration-docs"    element={<PrivateRoute feature="integration_docs"><Layout><IntegrationDocs /></Layout></PrivateRoute>} />
          {/* Onda 66: o log ganhou caminho próprio, com permissão própria. A aba
              dentro de /api-integration continua existindo para o superadmin. */}
          <Route path="/api-request-log"     element={<PrivateRoute feature="api_request_log"><Layout><ApiRequestLogPage /></Layout></PrivateRoute>} />
          {/* Onda 109 */}
          <Route path="/integration-loads"   element={<PrivateRoute feature="integration_loads"><Layout><IntegrationLoads /></Layout></PrivateRoute>} />
          {/* Onda 110 */}
          <Route path="/club-members"        element={<PrivateRoute feature="club_members"><Layout><ClubMembers /></Layout></PrivateRoute>} />
          {/* Administração */}
          <Route path="/price-tables"       element={<PrivateRoute feature="price_tables"><Layout><PriceTables /></Layout></PrivateRoute>} />
          <Route path="/users"              element={<PrivateRoute feature="users_manage"><Layout><Users /></Layout></PrivateRoute>} />
          <Route path="/settings"           element={<PrivateRoute feature="settings_view"><Layout><Settings /></Layout></PrivateRoute>} />
          {/* Fase 5 — API de Integração */}
          <Route path="/api-integration"    element={<PrivateRoute feature="api_integration"><Layout><ApiIntegration /></Layout></PrivateRoute>} />
          <Route path="/gate-log"           element={<PrivateRoute feature="gate_log"><Layout><GateLog /></Layout></PrivateRoute>} />
          <Route path="/ocr-quality"        element={<PrivateRoute feature="ocr_quality"><Layout><OcrQuality /></Layout></PrivateRoute>} />
          <Route path="/saidas"             element={<PrivateRoute feature="exit_release"><Layout><Saidas /></Layout></PrivateRoute>} />
          <Route path="/reconciliation"     element={<PrivateRoute feature="reconciliation"><Layout><Reconciliation /></Layout></PrivateRoute>} />
          <Route path="/quarantine"         element={<PrivateRoute feature="quarantine"><Layout><Quarantine /></Layout></PrivateRoute>} />
          <Route path="/inventory"          element={<PrivateRoute feature="yard_inventory"><Layout><Inventory /></Layout></PrivateRoute>} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}
export default App;
